import {useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {GeneralStatistics} from '../../../redux/Slice/generalSlice'
import {Link} from 'react-router-dom'
import {useAuth} from 'app/modules/auth'
import {ApexOptions} from 'apexcharts'
import ReactApexChart from 'react-apexcharts'
import LecStatistics from './LecturerStatistics'
import StdStatistics from './StdStatistics'

interface StudentCounts {
  total_applications: number
  total_students: number
  total_alumni: number
}

interface FinanceData {
  total_paid?: number
  total_unpaid?: number
  total_invoices?: number
}

interface EvaluationData {
  academic_year_id: number
  academic_year_name: string
  strongly_agree_count: number
  agree_count: number
  strongly_disagree_count: number
  somewhat_agree_count: number
  formatted_created_at?: string
}
const AdminDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {hasPermission} = useAuth()
  const [studentCounts, setStudentCounts] = useState<StudentCounts | null>(null)
  const [finance, setFinance] = useState<FinanceData>({})
  const [showLecturerCharts, setShowLecturerCharts] = useState(false)
  const [showStudentCharts, setShowStudentCharts] = useState(false)
  const [showEvaluationCharts, setShowEvaluationCharts] = useState(false)
  const [evaluationData, setEvaluationData] = useState<EvaluationData[]>([])

  useEffect(() => {
    dispatch(GeneralStatistics()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setStudentCounts(res.payload.student_counts || null)
        setFinance(res.payload.finance || {})
        setEvaluationData(res.payload.academicYearEvForm || [])
      }
      setLoader(false)
    })
  }, [])

  const parseBackendData = (data: any[]): EvaluationData[] => {
    return data.map((item) => ({
      ...item,
      strongly_agree_count: Number(item.strongly_agree_count) || 0,
      agree_count: Number(item.agree_count) || 0,
      strongly_disagree_count: Number(item.strongly_disagree_count) || 0,
      somewhat_agree_count: Number(item.somewhat_agree_count) || 0,
    }))
  }

  const processEvaluationData = (data: EvaluationData[]) => {
    return data.map((year) => {
      const total =
        year.strongly_agree_count +
        year.agree_count +
        year.strongly_disagree_count +
        year.somewhat_agree_count

      return {
        ...year,
        strongly_agree_percent: total > 0 ? (year.strongly_agree_count / total) * 100 : 0,
        agree_percent: total > 0 ? (year.agree_count / total) * 100 : 0,
        strongly_disagree_percent: total > 0 ? (year.strongly_disagree_count / total) * 100 : 0,
        somewhat_agree_percent: total > 0 ? (year.somewhat_agree_count / total) * 100 : 0,
        total_responses: total,
      }
    })
  }

  const processedData = processEvaluationData(parseBackendData(evaluationData))

  const evaluationSeries = [
    {
      name: 'Excellent',
      data: processedData.map((y) => y.strongly_agree_percent),
    },
    {
      name: 'Très bon/ Very Good',
      data: processedData.map((y) => y.agree_percent),
    },
    {
      name: 'Pas Satisfaisant/ Not Satisfactory',
      data: processedData.map((y) => y.somewhat_agree_percent),
    },
    {
      name: 'Pas de tout satisfaisant/ Not at all Satisfactory',
      data: processedData.map((y) => y.strongly_disagree_percent),
    },
  ]

  // Chart options
  const evaluationOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 400,
      stacked: false,
      toolbar: {
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    colors: ['#4CAF50', '#8BC34A', '#FFC107', '#F44336'],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 6,
        dataLabels: {
          position: 'top',
          hideOverflowingLabels: false,
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => (val > 0 ? `${val.toFixed(val < 5 ? 1 : 0)}%` : ''),
      style: {
        fontSize: '11px',
        fontWeight: 'bold',
        colors: ['#fff'],
      },
    },
    xaxis: {
      categories: processedData.map((y) => y.academic_year_name),
      labels: {
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (val: number) => `${val}%`,
      },
    },
    tooltip: {
      shared: false,
      y: {
        formatter: (val: number, {seriesIndex, dataPointIndex}) => {
          const year = processedData[dataPointIndex]
          const counts = [
            year.strongly_agree_count,
            year.agree_count,
            year.somewhat_agree_count,
            year.strongly_disagree_count,
          ]
          return `${val.toFixed(1)}% (${counts[seriesIndex]} of ${year.total_responses})`
        },
      },
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      markers: {
        radius: 12,
        width: 12,
        height: 12,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 450,
          },
          plotOptions: {
            bar: {
              columnWidth: '45%',
            },
          },
          dataLabels: {
            style: {
              fontSize: '9px',
            },
          },
        },
      },
    ],
  }

  if (loader) return <Loader />

  return (
    <div className='container-fluid px-4'>
      {/* Student Statistics Cards */}
      {studentCounts && (
        <div className='row g-4'>
          {/* Applications Card */}
          <div className='col-md-4'>
            <Link
              to={
                hasPermission('student-applications-list')
                  ? '/student/student-applications-list'
                  : '#'
              }
              className='text-decoration-none'
            >
              <div className='card bg-primary text-white bg-gradient shadow h-100 rounded-4'>
                <div className='card-body d-flex align-items-center'>
                  <i className='fas fa-file-alt mx-4' style={{fontSize: '3rem'}}></i>
                  <div>
                    <div className='fs-1 fw-bold'>{studentCounts.total_applications}</div>
                    <div className='fs-3'>{t('DASHBOARD.TOTAL_APPLICATIONS')}</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Students Card */}
          <div className='col-md-4'>
            <Link
              to={hasPermission('student-applications-list') ? '/student/students-list' : '#'}
              className='text-decoration-none'
            >
              <div className='card bg-success text-white bg-gradient shadow h-100 rounded-4'>
                <div className='card-body d-flex align-items-center'>
                  <i className='fas fa-user-graduate mx-4' style={{fontSize: '3rem'}}></i>
                  <div>
                    <div className='fs-1 fw-bold'>{studentCounts.total_students}</div>
                    <div className='fs-3'>{t('DASHBOARD.TOTAL_STUDENTS')}</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Alumni Card */}
          <div className='col-md-4'>
            <Link
              to={
                hasPermission('student-applications-list') ? '/student/alumni-students-list' : '#'
              }
              className='text-decoration-none'
            >
              <div className='card bg-info text-white bg-gradient shadow h-100 rounded-4'>
                <div className='card-body d-flex align-items-center'>
                  <i className='fas fa-user-tie mx-4' style={{fontSize: '3rem'}}></i>
                  <div>
                    <div className='fs-1 fw-bold'>{studentCounts.total_alumni}</div>
                    <div className='fs-3'>{t('DASHBOARD.TOTAL_ALUMNI')}</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Finance Statistics */}
      <div className='row g-2 mt-4'>
        <div className='col-md-4'>
          <div className='card text-white bg-success p-2 h-100'>
            <h6 className='card-title mb-1'>{t('LABEL.TOTAL_PAID')}</h6>
            <span className='badge bg-light text-dark fs-6'>{finance?.total_paid ?? 0}</span>
          </div>
        </div>
        <div className='col-md-4'>
          <div className='card text-white bg-danger p-2 h-100'>
            <h6 className='card-title mb-1'>{t('LABEL.TOTAL_DUE')}</h6>
            <span className='badge bg-light text-dark fs-6'>{finance?.total_unpaid ?? 0}</span>
          </div>
        </div>
        <div className='col-md-4'>
          <div className='card text-white bg-warning p-2 h-100'>
            <h6 className='card-title mb-1'>{t('LABEL.TOTAL_AMOUNT')}</h6>
            <span className='badge bg-light text-dark fs-6'>{finance?.total_invoices ?? 0}</span>
          </div>
        </div>
      </div>

      <div className='card shadow-sm h-100 mt-5'>
        <div
          className='card-header bg-primary'
          style={{minHeight: '30px'}}
          onClick={() => setShowLecturerCharts(!showLecturerCharts)}
        >
          <h3 className='text-white mt-3'>{t('DASHBOARD.PROFESSOR_OVERVIEW')}</h3>
          <button type='button' className='btn btn-link btn-sm text-white'>
            <span className={`fa fa-caret-${showLecturerCharts ? 'down' : 'up'}`}></span>
          </button>
        </div>
        {showLecturerCharts && (
          <div className='card-body'>
            <LecStatistics />
          </div>
        )}
      </div>

      <div className='card shadow-sm h-100 mt-5'>
        <div
          className='card-header bg-primary'
          style={{minHeight: '30px'}}
          onClick={() => setShowStudentCharts(!showStudentCharts)}
        >
          <h3 className='text-white mt-3'>{t('DASHBOARD.STUDENTS_PROFILE')}</h3>
          <button type='button' className='btn btn-link btn-sm text-white'>
            <span className={`fa fa-caret-${showStudentCharts ? 'down' : 'up'}`}></span>
          </button>
        </div>
        {showStudentCharts && (
          <div className='card-body'>
            <StdStatistics />
          </div>
        )}
      </div>

      <div className='card shadow-sm h-100 mt-5'>
        <div
          className='card-header bg-primary'
          style={{minHeight: '30px'}}
          onClick={() => setShowEvaluationCharts(!showEvaluationCharts)}
        >
          <h3 className='text-white mt-3'>{t('LABEL.FEEDBACK_AND_EVALUATION')}</h3>
          <button type='button' className='btn btn-link btn-sm text-white'>
            <span className={`fa fa-caret-${showEvaluationCharts ? 'down' : 'up'}`}></span>
          </button>
        </div>
        {showEvaluationCharts && (
          <div className='card-body'>
            {evaluationData.length > 0 ? (
              <>
                <ReactApexChart
                  options={evaluationOptions}
                  series={evaluationSeries}
                  type='bar'
                  height={350}
                  width={'100%'}
                />
              </>
            ) : (
              <div className='text-center py-5'>No evaluation data available</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminDashboard
